import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';
import { DataTableDirective, DataTablesModule } from 'angular-datatables';
import { Config } from 'datatables.net';
import { Subject } from 'rxjs';
import { Article } from '../../models/article';
import { ArticleService } from '../../services/article.service';
import { AuthenticationService } from '../../services/auth/authentication.service';
import { ModalService } from '../../services/modal/modal.service';

@Component({
  selector: 'app-articles',
  imports: [DataTablesModule, CommonModule, RouterModule],
  templateUrl: './articles.component.html',
  styleUrl: './articles.component.scss',
})
export class ArticlesComponent implements OnInit {
  dtOptions!: Config;

  articles!: Article[];

  @ViewChild(DataTableDirective)
  datatableElement!: DataTableDirective;
  dtTrigger: Subject<any> = new Subject<any>();

  fullyLoaded: boolean = false;

  constructor(
    private articleService: ArticleService,
    private authService: AuthenticationService,
    private modalService: ModalService,
    private vcr: ViewContainerRef,
    private router: Router,
    private destroyRef: DestroyRef,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.articleService
      .getArticles()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp) => {
          this.articles = resp;
          this.fullyLoaded = true;
          this.changeDetector.detectChanges();
          this.dtTrigger.next(null);
        },
        error: (err: HttpErrorResponse) => this.authService.logout(),
      });

    this.dtOptions = {
      pagingType: 'full_numbers',
      destroy: true,
      search: {
        caseInsensitive: true,
        smart: true,
      },
      ajax: (dataTablesParameters: any, callback) => {
        callback({ data: this.articles });
      },
      lengthMenu: [5, 10, 20, 50],
      columns: [
        {
          title: 'Name',
          data: 'name',
        },
        {
          title: 'Unit of measurement',
          data: 'unitOfMeasurement',
        },
        {
          title: 'Price',
          data: 'price',
        },
        {
          title: 'Status',
          data: 'active',
          render: (data, type, row, meta) => {
            return data == 1
              ? '<td><i class="mdi mdi-circle text-success"></i> Active</td>'
              : ' <td><i class="mdi mdi-circle text-danger"></i> Inactive</td>';
          },
        },
        {
          title: 'Action',
          data: null,
          orderable: false,
          render: (data, type, row, meta) => {
            return `<a href="javascript: void(0);" id="edit" class="action-icon"> <i class="mdi mdi-pencil"></i></a><a href="javascript: void(0);" id="delete" class="action-icon"> <i class="mdi mdi-delete"></i></a>`;
          },
        },
      ],
      rowCallback: (row: Node, data: any[] | object, index: number) => {
        $('td:last-child #delete', row).off('click');
        $('td:last-child #edit', row).off('click');
        $('td:last-child #delete', row).on('click', () =>
          this.deleteArticle((data as Article).id)
        );
        $('td:last-child #edit', row).on('click', () =>
          this.editArticle(data as Article)
        );
      },
    };
  }

  ngAfterViewInit(): void {
    this.dtTrigger.next(null);
  }

  deleteArticle(id: number) {
    this.modalService
      .openYesNoDialog(this.vcr)
      .getAnswer()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (answer) => {
          if (answer) {
            this.articleService
              .deleteArticle(id)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe({
                next: () => {
                  this.articles = this.articles.filter(
                    (article: Article) => article.id !== id
                  );
                  this.dtTrigger.next(null);
                },
                error: (err: HttpErrorResponse) => {
                  if (err.status == 401) {
                    this.authService.logout();
                  } else {
                    this.modalService.showErrorDialog(
                      this.vcr,
                      err.error.message
                    );
                  }
                },
              });
          }
        },
      });
  }

  editArticle(article: Article) {
    localStorage.setItem('edit', JSON.stringify(article));
    this.router.navigateByUrl('dashboard/articles/update');
  }
}
